/**
 * GovDashboardPage — Government contracting home.
 * Shows pipeline counts, upcoming solicitation deadlines, recent awards.
 * Route: /gov
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
import api from '../../services/api';

type Dashboard = {
  counts: {
    sources_sought_total: number;
    active_total: number;
    construction_total: number;
  };
  ss_by_stage: Record<string, number>;
  active_by_stage: Record<string, number>;
  construction_by_stage: Record<string, number>;
  upcoming_deadlines: Array<{
    id: string; title: string; response_deadline: string;
    pipeline: string; active_stage?: string; ss_stage?: string;
  }>;
  recent_awards: Array<{ id: string; title: string; award_amount?: number; award_date?: string }>;
};

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 24, background: '#0F1923', minHeight: '100vh', color: '#E5EDF5' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 26, fontWeight: 800, margin: 0, color: '#fff' },
  subtitle: { color: '#8FA8C0', fontSize: 14, marginTop: 4 },
  bigCards: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 },
  bigCard: { background: '#1A2B3C', padding: 24, borderRadius: 12, borderLeft: '4px solid #4A7EB5', textDecoration: 'none', color: 'inherit' },
  bigNum: { fontSize: 36, fontWeight: 800, color: '#fff', marginTop: 8 },
  bigLabel: { color: '#8FA8C0', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 },
  panelGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  panel: { background: '#1A2B3C', padding: 20, borderRadius: 12 },
  panelTitle: { fontSize: 14, fontWeight: 700, color: '#fff', margin: 0, marginBottom: 14 },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #243447', textDecoration: 'none', color: 'inherit' },
  empty: { color: '#526980', fontStyle: 'italic', padding: 12, textAlign: 'center' as const },
  deadlineChip: { padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 },
  loading: { padding: 40, textAlign: 'center' as const, color: '#8FA8C0' },
};

const deadlineColor = (days: number): string => {
  if (days < 7) return '#EF4444';
  if (days < 14) return '#F97316';
  if (days < 30) return '#EAB308';
  return '#4A7EB5';
};

const fmt = (d?: string | null) => { try { return d ? format(parseISO(d), 'MMM d, yyyy') : ''; } catch { return ''; } };
const money = (n?: number | null) => (n == null ? '' : `$${Math.round(n).toLocaleString()}`);

const GovDashboardPage: React.FC = () => {
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/gov/dashboard');
        setData(data);
      } catch (e) {
        console.error('Dashboard load failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div style={styles.container}><div style={styles.loading}>Loading dashboard…</div></div>;
  if (!data) return <div style={styles.container}><div style={styles.loading}>No data</div></div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🎯 Government Contracting</h1>
          <div style={styles.subtitle}>McMullen Browne Development LLC · UEI XHBRUBKCMP45 · 8(a) SB</div>
        </div>
      </div>

      <div style={styles.bigCards}>
        <Link to="/gov/solicitations?pipeline=sources_sought" style={{ ...styles.bigCard, borderLeftColor: '#8B5CF6' }}>
          <div style={styles.bigLabel}>Sources Sought / RFI</div>
          <div style={styles.bigNum}>{data.counts.sources_sought_total}</div>
          <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 4 }}>Pre-solicitation positioning</div>
        </Link>
        <Link to="/gov/solicitations?pipeline=active" style={{ ...styles.bigCard, borderLeftColor: '#4A7EB5' }}>
          <div style={styles.bigLabel}>Active Solicitations</div>
          <div style={styles.bigNum}>{data.counts.active_total}</div>
          <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 4 }}>RFQ / RFP / IFB in flight</div>
        </Link>
        <Link to="/gov/construction" style={{ ...styles.bigCard, borderLeftColor: '#10B981' }}>
          <div style={styles.bigLabel}>Construction Jobs</div>
          <div style={styles.bigNum}>{data.counts.construction_total}</div>
          <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 4 }}>Post-award, in execution</div>
        </Link>
      </div>

      <div style={styles.panelGrid}>
        <div style={styles.panel}>
          <div style={styles.panelTitle}>⏰ Upcoming Deadlines</div>
          {data.upcoming_deadlines.length === 0 && <div style={styles.empty}>No upcoming deadlines.</div>}
          {data.upcoming_deadlines.map((d) => {
            const days = differenceInCalendarDays(parseISO(d.response_deadline), new Date());
            return (
              <Link key={d.id} to={`/gov/solicitations/${d.id}`} style={styles.row}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{d.title.slice(0, 60)}</div>
                  <div style={{ fontSize: 12, color: '#8FA8C0' }}>{fmt(d.response_deadline)}</div>
                </div>
                <div style={{ ...styles.deadlineChip, background: deadlineColor(days), color: '#fff' }}>
                  {days <= 0 ? 'TODAY' : `${days}d`}
                </div>
              </Link>
            );
          })}
        </div>

        <div style={styles.panel}>
          <div style={styles.panelTitle}>🏆 Recent Awards</div>
          {data.recent_awards.length === 0 && <div style={styles.empty}>No awards yet.</div>}
          {data.recent_awards.map((a) => (
            <div key={a.id} style={styles.row}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{a.title.slice(0, 60)}</div>
                <div style={{ fontSize: 12, color: '#8FA8C0' }}>{fmt(a.award_date)}</div>
              </div>
              <div style={{ color: '#10B981', fontWeight: 700 }}>{money(a.award_amount)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GovDashboardPage;
